import {
  IconName,
  useCurrenciesContext,
  useMarketsContext,
  useSecuritiesContext,
  type FilterableSelectProperty,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import type { PriceGridFilterState } from '../types';
import {
  getGenericPriceGridBasePairOptions,
  getGenericPriceGridCurrencyOptions,
  getGenericPriceGridMarketOptions,
} from './options';

const getStringOptionLabel = (option: string) => option;
const EMPTY_STRING_SELECTIONS: string[] = [];

export interface UsePriceGridFilterControlsParams {
  filterState: PriceGridFilterState;
  onFilterStateChanged: (filterState: PriceGridFilterState) => void;
}

export function usePriceGridFilterControls({ filterState, onFilterStateChanged }: UsePriceGridFilterControlsParams) {
  const { currenciesList } = useCurrenciesContext();
  const { marketsByName, marketsList } = useMarketsContext();
  const { securitiesList } = useSecuritiesContext();

  const selectedMarkets = filterState.markets ?? EMPTY_STRING_SELECTIONS;
  const selectedCurrencies = filterState.currencies ?? EMPTY_STRING_SELECTIONS;
  const selectedBasePairs = filterState.basePairs ?? EMPTY_STRING_SELECTIONS;

  const marketOptions = useMemo(() => getGenericPriceGridMarketOptions(marketsList), [marketsList]);
  const currencyOptions = useMemo(() => getGenericPriceGridCurrencyOptions(currenciesList), [currenciesList]);
  const basePairOptions = useMemo(() => getGenericPriceGridBasePairOptions(securitiesList), [securitiesList]);

  const getMarketOptionLabel = useCallback(
    (market: string) => marketsByName.get(market)?.DisplayName ?? market,
    [marketsByName]
  );

  const marketsProperty = useMemo(
    (): FilterableSelectProperty<'markets', string> => ({
      key: 'markets',
      label: 'Markets',
      icon: IconName.OfficeBuilding,
      options: marketOptions,
      getOptionLabel: getMarketOptionLabel,
      clearUnsupportedSelectionsOnInit: false,
      dropdownOptions: { dropdownWidth: 220 },
    }),
    [getMarketOptionLabel, marketOptions]
  );

  const currenciesProperty = useMemo(
    (): FilterableSelectProperty<'currencies', string> => ({
      key: 'currencies',
      label: 'Currency',
      icon: IconName.CurrencyDollar,
      options: currencyOptions,
      getOptionLabel: getStringOptionLabel,
      clearUnsupportedSelectionsOnInit: false,
      dropdownOptions: { dropdownWidth: 180 },
    }),
    [currencyOptions]
  );

  const basePairsProperty = useMemo(
    (): FilterableSelectProperty<'basePairs', string> => ({
      key: 'basePairs',
      label: 'Base Pair',
      icon: IconName.ArrowLeftRight,
      options: basePairOptions,
      getOptionLabel: getStringOptionLabel,
      clearUnsupportedSelectionsOnInit: false,
      dropdownOptions: { dropdownWidth: 200 },
    }),
    [basePairOptions]
  );

  const updateStringSelections = useCallback(
    (filterKey: 'markets' | 'currencies' | 'basePairs', selections: string[]) => {
      const nextSelections = [...new Set(selections)];
      onFilterStateChanged({
        ...filterState,
        [filterKey]: nextSelections.length === 0 ? undefined : nextSelections,
      });
    },
    [filterState, onFilterStateChanged]
  );

  const setMarkets = useCallback(
    (markets: string[]) => updateStringSelections('markets', markets),
    [updateStringSelections]
  );
  const setCurrencies = useCallback(
    (currencies: string[]) => updateStringSelections('currencies', currencies),
    [updateStringSelections]
  );
  const setBasePairs = useCallback(
    (basePairs: string[]) => updateStringSelections('basePairs', basePairs),
    [updateStringSelections]
  );

  return useMemo(
    () => ({
      markets: {
        property: marketsProperty,
        selections: selectedMarkets,
        onSelectionsChange: setMarkets,
      },
      currencies: {
        property: currenciesProperty,
        selections: selectedCurrencies,
        onSelectionsChange: setCurrencies,
      },
      basePairs: {
        property: basePairsProperty,
        selections: selectedBasePairs,
        onSelectionsChange: setBasePairs,
      },
    }),
    [
      basePairsProperty,
      currenciesProperty,
      marketsProperty,
      selectedBasePairs,
      selectedCurrencies,
      selectedMarkets,
      setBasePairs,
      setCurrencies,
      setMarkets,
    ]
  );
}
